<template>
  <div class="marketing-opt-in-screen">
    <!-- Loading State -->
    <div v-if="isLoading" class="loading-message">
      {{ t('labels.Loading') }}...
    </div>
    
    <!-- Error State -->
    <div v-else-if="error" class="error-message">
      <p>{{ error }}</p>
      <button @click="loadOptInPreferences" class="retry-button">
        {{ t('buttons.Retry') }}
      </button>
    </div>
    
    <!-- Opt-In Preferences List -->
    <div v-if="optInItems.length > 0" class="opt-in-container">
      <div class="opt-in-header">
        <h3>{{ t('accountInfo.marketingOptIn.title') }}</h3>
        <p class="opt-in-description">{{ t('accountInfo.marketingOptIn.description') }}</p>
      </div>
      
      <div class="opt-in-list">
        <div 
          v-for="item in optInItems" 
          :key="item.codeIdCampaignType.codeId"
          class="opt-in-item"
        >
          <div class="opt-in-content">
            <div class="opt-in-info">
              <h4 class="opt-in-name">{{ item.displayName }}</h4>
              <div v-if="item.hasExistingData" class="opt-in-last-modified">
                <small>{{ t('accountInfo.marketingOptIn.lastModified') }}: {{ formatDate(item.lastModifiedDate) }}</small>
              </div>
            </div>
            <div class="opt-in-toggle">
              <label class="toggle-switch">
                <input 
                  type="checkbox" 
                  :checked="item.isOptedIn"
                  @change="toggleOptIn(item.codeIdCampaignType.codeId)"
                  :disabled="isSaving"
                />
                <span class="toggle-slider"></span>
              </label>
              <span class="toggle-label">
                {{ item.isOptedIn ? t('accountInfo.marketingOptIn.optedIn') : t('accountInfo.marketingOptIn.optedOut') }}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Save Button -->
      <div class="save-section">
        <button 
          @click="saveOptInPreferences" 
          :disabled="isSaving"
          class="save-button"
        >
          <span v-if="isSaving">{{ t('labels.Saving') }}...</span>
          <span v-else>{{ t('buttons.Save') }}</span>
        </button>
      </div>
    </div>
    
    <!-- No Opt-In Options Available -->
    <div v-else class="no-opt-ins">
      <div class="no-opt-ins-content">
        <h3>{{ t('accountInfo.marketingOptIn.noOptionsTitle') }}</h3>
        <p>{{ t('accountInfo.marketingOptIn.noOptionsDescription') }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMarketingOptIn } from '../../composables/useMarketingOptIn'
import Swal from 'sweetalert2'

const { t } = useI18n()

// Get composable
const {
  // State
  optInItems,
  isLoading,
  isSaving,
  error,
  successMessage,
  
  // Methods
  loadOptInPreferences,
  saveOptInPreferences,
  toggleOptIn,
  clearSuccessMessage
} = useMarketingOptIn()

// Format date for display
function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString()
  } catch {
    return dateString
  }
}

// Watch for success messages and show Swal popup
watch(successMessage, (newMessage) => {
  if (newMessage) {
    Swal.fire({
      title: t('labels.Success'),
      text: newMessage,
      icon: 'success',
      confirmButtonText: t('buttons.Okay')
    }).then(() => {
      // Clear the success message after showing the popup
      clearSuccessMessage()
    })
  }
})

onMounted(async () => {
  await loadOptInPreferences()
})
</script>

<style lang="scss" scoped>
.marketing-opt-in-screen {
  width: 100%;
  padding: $card-padding;
}

.loading-message {
  @include typography-body;
  text-align: center;
  padding: $spacing-10;
  color: #666;
}

.error-message {
  text-align: center;
  padding: $spacing-10;
  color: #dc3545;
}

.success-message {
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
  padding: $spacing-3 $spacing-4;
  border-radius: $radius-sm;
  margin-bottom: 20px;
}

.success-message p {
  margin: 0;
}

.retry-button {
  margin-top: 16px;
  padding: $spacing-2 $spacing-4;
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
  border: none;
  @include typography-small;
  border-radius: $button-radius-lg;
  cursor: pointer;
}

.retry-button:hover {
  background-color: var(--buttonPrimaryHoverColor, #145258);
}

.opt-in-container {
  width: 100%;
}

.opt-in-header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.opt-in-header h3 {
  @include typography-h4;
  margin: 0 0 12px 0;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.opt-in-description {
  @include typography-small;
  margin: 0;
  color: #666;
}

.opt-in-list {
  display: flex;
  flex-direction: column;
  gap: $gap-md;
  margin-bottom: 32px;
}

.opt-in-item {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: $radius-md;
  padding: $card-padding;
  box-shadow: 0 2px 4px rgba(black, 0.1);
  transition: box-shadow 0.2s ease;
}

.opt-in-item:hover {
  box-shadow: 0 4px 8px rgba(black, 0.2);
}

.opt-in-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: $spacing-5;
}

.opt-in-info {
  flex: 1;
}

.opt-in-name {
  @include typography-body;
  @include font-weight-semibold;
  margin: 0 0 8px 0;
  color: #333;
}

.opt-in-item-description {
  @include typography-small;
  margin: 0 0 12px 0;
  color: #666;
}

.opt-in-category {
  margin-bottom: 8px;
}

.category-badge {
  background-color: #e9f5f7;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  padding: $spacing-1 $spacing-2;
  @include typography-caption;
  @include font-weight-medium;
  border-radius: $radius-lg;
}

.opt-in-last-modified {
  @include typography-caption;
  color: #888;
}

.opt-in-toggle {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $gap-sm;
  flex-shrink: 0;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cccccc;
  transition: 0.3s;
  border-radius: $radius-2xl;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
}

input:checked + .toggle-slider {
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

input:focus + .toggle-slider {
  box-shadow: 0 0 1px var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

input:checked + .toggle-slider:before {
  transform: translateX(26px);
}

input:disabled + .toggle-slider {
  opacity: 0.6;
  cursor: not-allowed;
}

.toggle-label {
  @include typography-caption;
  color: #666;
  text-align: center;
  min-width: 60px;
}

.save-section {
  text-align: center;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
}

.save-button {
  padding: $spacing-3 $spacing-8;
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
  border: none;
  @include typography-body;
  @include font-weight-medium;
  border-radius: $button-radius-lg;
  cursor: pointer;
  transition: all 0.2s ease;
}

.save-button:hover:not(:disabled) {
  background-color: var(--buttonPrimaryHoverColor, #145258);
  transform: translateY(-1px);
}

.save-button:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
  opacity: 0.6;
}

.no-opt-ins {
  text-align: center;
  padding: $spacing-16 $spacing-5;
}

.no-opt-ins-content h3 {
  color: #666;
  margin-bottom: 12px;
}

.no-opt-ins-content p {
  @include typography-small;
  color: #888;
}

@media (max-width: 768px) {
  .marketing-opt-in-screen {
    padding: $spacing-4;
  }
  
  .opt-in-content {
    flex-direction: column;
    gap: $gap-md;
    align-items: flex-start;
  }
  
  .opt-in-toggle {
    flex-direction: row;
    align-items: center;
    align-self: flex-start;
  }
  
  .toggle-label {
    min-width: auto;
  }
}
</style>
